import React from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageLoader, PageHeader } from "@/components/shared";
import { Disclaimer } from "@/components/Disclaimer";
import { JOURNEY_STAGES, stageIndex, currentStageKey } from "@/lib/journey";
import { Check, KeyRound } from "lucide-react";

export default function Journey() {
  const { data, isLoading } = useQuery({
    queryKey: ["workspace"],
    queryFn: async () => (await api.get("/buyer/workspace")).data,
  });
  if (isLoading) return <PageLoader />;

  const client = data.client;
  const curKey = currentStageKey(client);
  const curIdx = stageIndex(curKey);

  return (
    <div>
      <PageHeader title="Your home-buying journey" subtitle="Here's where you are and what's coming next. Your agent keeps this up to date as you move forward." />

      <div className="relative" data-testid="journey-timeline">
        {JOURNEY_STAGES.map((s, i) => {
          const done = i < curIdx;
          const current = i === curIdx;
          const last = i === JOURNEY_STAGES.length - 1;
          return (
            <motion.div key={s.key} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
              className="flex gap-4" data-testid={`journey-stage-${s.key}`}>
              {/* rail */}
              <div className="flex flex-col items-center">
                <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 border-2 transition-colors ${
                  done ? "bg-status-complete border-status-complete text-white"
                    : current ? "bg-primary border-primary text-primary-foreground ring-4 ring-primary/20"
                      : "bg-background border-border text-muted-foreground"}`}>
                  {done ? <Check className="h-4 w-4" /> : last ? <KeyRound className="h-4 w-4" /> : <span className="text-sm font-semibold">{i + 1}</span>}
                </div>
                {!last && <div className={`w-0.5 flex-1 min-h-[28px] ${i < curIdx ? "bg-status-complete" : "bg-border"}`} />}
              </div>
              {/* content */}
              <div className={`pb-6 flex-1 ${last ? "" : ""}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className={`font-heading font-semibold ${current ? "text-primary" : done ? "" : "text-muted-foreground"}`}>{s.label}</p>
                  {current && <Badge className="bg-primary/15 text-primary border-0">You're here</Badge>}
                  {done && <Badge className="bg-status-complete/15 text-status-complete border-0">Done</Badge>}
                </div>
                {(current || !done) && <p className="text-sm text-muted-foreground mt-1 max-w-xl">{s.nudge}</p>}
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-4">
        <Disclaimer>This timeline shows your general progress. It isn't legal, tax, or financial advice — your agent, lender, and licensed professionals guide each step.</Disclaimer>
      </div>
    </div>
  );
}
